import React, { useEffect, useRef, useState } from 'react';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { xlate, xlationContext } from '../../../../framework/utils/i18n';
import * as formService from '../../form-services/presentation-editor';
import { PresentationApiClient } from '../../../../services/presentation';
import { GetPresentationForEditingResponse } from '../../../../entities-generated/models/presentation/get-presentation-for-editing-response';
import { PresentationEditor } from '../../components/presentation-editor';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { MessageBarType } from '@fluentui/react';
import { useNavigator } from '../../../../framework/services/navigation';
import { AppRoutes } from '../../../app-routes';
import { useStandardErrorState } from '../../../../entities/view-manager-standard/error';

xlationContext('Create presentation page');

const subscriberId = 'Page.CreatePresentationPage';

export const CreatePresentationPage: React.FunctionComponent = () => {
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const formModel = formService.useFormModel(subscriberId);
  const presentationId = useRef<string | undefined>(undefined);
  const errorState = useStandardErrorState();
  const { showToast } = useToast();
  const navigator = useNavigator();

  // initialize page
  useEffect(() => {
    PresentationApiClient.getSearchableAccountPresentationTitles()
    .then(titleList => {
      const dataModel = new GetPresentationForEditingResponse();
      dataModel.existingTitles = titleList;
      formService.initializeNewPresentation(dataModel);
      setViewState(BwjViewState.Ready);
    })
    .catch(() => {
      setViewState(BwjViewState.Error);
    });
  }, []);

  // moves user to edit presentation page after a successful save
  // navigator, showToast never change during component's lifetime
  useEffect(() => {
    if(!!presentationId.current) {
      showToast(MessageBarType.success, xlate('Discussion plan saved successfully'));
      navigator.goTo(AppRoutes.updatePresentation.url(presentationId.current));
    }
  }, [formModel, navigator, showToast]);

  const onSavePresentation = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid) {
        const request = formService.toCreateRequest();
        return PresentationApiClient.createPresentation(request);
      } else {
        return undefined;
      }
    })
    .then(id => {
      if(id === undefined) { return; }

      presentationId.current = id.value;
      formService.markAllChangesSaved();
    })
    .catch(() => {
      showToast(MessageBarType.error, xlate('An error occurred while saving - please try again'), true);
    });
  };

  return (
    <BwjViewManager
      state={viewState}
      loadingState={{ message: xlate('One moment please...') }}
      errorState={errorState}
    >
      <PresentationEditor
        formModel={formModel}
        onSavePresentation={onSavePresentation}
        savingPresentation={false}
      />
    </BwjViewManager>
  );
};
